import axiosClient from '../api/axiosClient';
import API from '../Constants/apiRoutes';

const cfoService = {
  /**
   * GET /admin/cfo/dashboard?currency=USD&period=MonthToDate.
   * period: MonthToDate (default) | QuarterToDate | YearToDate | AllTime.
   * Billed/collected follow the period; outstanding/overdue stay point-in-time.
   */
  getDashboard: async (currency = 'USD', period = 'MonthToDate') => {
    const { data } = await axiosClient.get(API.cfoDashboard, { params: { currency, period } });
    return data;
  },

  /** GET /admin/cfo/pricing-plans?includeInactive= */
  getPricingPlans: async (includeInactive = false) => {
    // Backend contract (confirmed by Mudassir): includeInactive shows all plans;
    // new-client selectors pass includeInactive=false to get active-only plans.
    const { data } = await axiosClient.get(API.pricingPlans.base, {
      params: { includeInactive },
    });
    return data;
  },

  /** POST /admin/cfo/pricing-plans */
  createPricingPlan: async (payload) => {
    const { data } = await axiosClient.post(API.pricingPlans.base, payload);
    return data;
  },

  /** PATCH /admin/cfo/pricing-plans/{id}/status -> { isActive } (deactivate/reactivate). */
  setPricingPlanStatus: async (id, isActive) => {
    const { data } = await axiosClient.patch(API.pricingPlans.status(id), { isActive });
    return data;
  },

  /** GET /admin/cfo/invoices/preview/{clientId} -> calculated lines + total (no record created). */
  previewInvoice: async (clientId) => {
    const { data } = await axiosClient.get(API.invoices.preview(clientId));
    return data;
  },

  /** POST /admin/cfo/invoices -> creates a Draft invoice. */
  createInvoice: async (payload) => {
    const { data } = await axiosClient.post(API.invoices.base, payload);
    return data;
  },

  /** GET /admin/cfo/invoices?clientId=&status=&page=&pageSize= -> paginated list. */
  getInvoices: async ({ clientId, status, page = 1, pageSize = 20 } = {}) => {
    const params = { page, pageSize };
    if (clientId) params.clientId = clientId;
    if (status !== undefined && status !== null && status !== '') params.status = status;
    const { data } = await axiosClient.get(API.invoices.base, { params });
    return data;
  },

  /** GET /admin/cfo/invoices/{invoiceId} -> invoice detail (server values authoritative). */
  getInvoice: async (invoiceId) => {
    const { data } = await axiosClient.get(API.invoices.detail(invoiceId));
    return data;
  },

  /** GET /admin/cfo/invoices/{invoiceId}/document -> PDF blob + server filename. */
  downloadInvoicePdf: async (invoiceId) => {
    const response = await axiosClient.get(API.invoices.document(invoiceId), {
      responseType: 'blob',
    });
    return response; // caller needs headers for the content-disposition filename
  },

  /** POST /admin/cfo/invoices/{invoiceId}/send -> { invoice, checkoutUrl }; moves to Sent. */
  sendInvoice: async (invoiceId) => {
    const { data } = await axiosClient.post(API.invoices.send(invoiceId));
    return data;
  },

  /** POST /admin/cfo/invoices/{invoiceId}/confirm-payment -> verified OFFLINE payment only. */
  confirmPayment: async (invoiceId, payload) => {
    const { data } = await axiosClient.post(API.invoices.confirmPayment(invoiceId), payload);
    return data;
  },
};

export default cfoService;
